import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect } from 'react';
import { v4 } from 'uuid';
import { Panel } from '../panels/panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { WorkspacePanelProvider } from '../panels/workspace-panel-provider';
import { EventPanelBody } from './event-panel-body';
import { IntegratedEventPanelForm } from './event-panel-form/integrated-event-panel-form';
import { EventPanelHeader } from './event-panel-header/event-panel-header';
import {
    ExandablePanelSizeProvider,
    useExpandablePanelSizeState,
} from '../panels/expandable-panel-size';
import { EventNotificationsEventIdProvider } from '../event-notifications/event-notifications-event-id-context';
import { EventNotificationsEntityIdProvider } from '../event-notifications/event-notifications-entity-id-context';
import { useAllEventNotifications } from '../event-notifications/use-event-notifications';
import { highestNotificationPriorityForNotifications } from '../event-notifications/highest-notification-priority-for-notifications';
import { EventPanelModalProvider } from './event-panel-modals';
import { ActiveEventPanelTabProvider } from './active-event-panel-tab-provider';
import { FormHasHadDataSetAtLeastOnceProvider } from './event-state/form-has-had-data-set-at-least-once';
import { RemovedEventPanelFormFieldsProvider } from './event-panel-form/form-persistence/use-locally-removed-fields';
import { EventPanelBannersProvider } from './event-panel-banners';
import { EventAdditionalInfosWrapper } from '../additional-infos/components/event-additional-infos-wrapper';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { CadNextAction } from '../action-broadcaster/types';
import { useEventMetadataStore } from './event-panel-form/event-metadata';
import { useBootstrapState } from '~/bootstrap/bootstrap-provider';

const EventPanelInner = observer(function EventPanelInner({
    panel,
}: {
    panel: WorkspacePanel<'EVENT'>;
}): React.ReactElement {
    const { resizeOptions, sizeProps } = useExpandablePanelSizeState(panel, 'EVENT');
    const { isFirstResponder } = useApplicationType();
    const { trigger } = useIntegratedActionBroadcaster();

    const allEventNotifications = useAllEventNotifications(panel.linkedEntityId ?? -1);
    const highestNotificationPriority = highestNotificationPriorityForNotifications(
        allEventNotifications
    );

    const phoneCallDto = panel.getPanelState('phoneCallDto');
    const eventMetadata = useEventMetadataStore().state.metadata;
    const phoneNumberId =
        phoneCallDto?.phoneNumberId ?? eventMetadata?.eventPhoneCall?.phoneNumberId;

    const { draftEventAlertTimer } = useBootstrapState();

    useEffect(() => {
        const enabled = draftEventAlertTimer > 0;
        const isDraft = !panel.linkedEntityId;

        if (enabled && isDraft) {
            panel.startInactivityTimer(draftEventAlertTimer);
        } else {
            panel.stopInactivityTimer();
        }

        return () => {
            panel.stopInactivityTimer();
        };
    }, [panel, draftEventAlertTimer, panel.linkedEntityId]);

    const handleEventPanelClose = useCallback(() => {
        if (!isFirstResponder && phoneNumberId) {
            trigger([
                {
                    id: v4(),
                    targetWindowType: 'MAP_V2',
                    type: 'CLEAR_AML_RAPIDSOS_ON_EVENT_CLOSE',
                } as CadNextAction,
            ]);
        }
    }, [isFirstResponder, phoneNumberId, trigger]);

    return (
        <Panel
            testId="EVENT_PANEL"
            panel={panel}
            onClose={handleEventPanelClose}
            header={<EventPanelHeader />}
            highestNotificationPriority={highestNotificationPriority}
            body={<EventPanelBody />}
            resizeOptions={resizeOptions}
            {...sizeProps}
            closeButtonProps={{ size: 'sm' }}
        />
    );
});

export const EventPanel = observer(function EventPanel({
    panel,
}: {
    panel: WorkspacePanel;
}): React.ReactElement {
    return (
        <WorkspacePanelProvider workspacePanel={panel}>
            <FormHasHadDataSetAtLeastOnceProvider>
                <ExandablePanelSizeProvider>
                    <EventPanelBannersProvider>
                        <EventPanelModalProvider>
                            <EventNotificationsEventIdProvider eventId={panel.linkedEntityId}>
                                <EventNotificationsEntityIdProvider entityId={panel.linkedEntityId}>
                                    <RemovedEventPanelFormFieldsProvider>
                                        <IntegratedEventPanelForm eventId={panel.linkedEntityId}>
                                            <ActiveEventPanelTabProvider>
                                                <EventAdditionalInfosWrapper>
                                                    <EventPanelInner panel={panel} />
                                                </EventAdditionalInfosWrapper>
                                            </ActiveEventPanelTabProvider>
                                        </IntegratedEventPanelForm>
                                    </RemovedEventPanelFormFieldsProvider>
                                </EventNotificationsEntityIdProvider>
                            </EventNotificationsEventIdProvider>
                        </EventPanelModalProvider>
                    </EventPanelBannersProvider>
                </ExandablePanelSizeProvider>
            </FormHasHadDataSetAtLeastOnceProvider>
        </WorkspacePanelProvider>
    );
});
